<template>
  <div class="mx-auto w-full max-w-7xl px-4 pb-16 pt-10 md:px-8 md:pt-16">
    <header class="mb-8">
      <p class="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">{{ $t('aiPage.kicker') }}</p>
      <h1 class="mt-3 text-3xl font-bold text-[var(--text-primary)] md:text-5xl">{{ $t('aiPage.title') }}</h1>
      <p class="mt-4 max-w-3xl text-[var(--text-muted)]">
        {{ $t('aiPage.subtitle') }}
      </p>
    </header>

    <section class="panel p-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-xl font-bold text-[var(--text-primary)]">{{ $t('aiProjects.title') }}</h2>
        <span class="rounded-full border border-[var(--border)] px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--accent)]">
          {{ $t('aiProjects.badge') }}
        </span>
      </div>
      <p class="mt-3 text-sm text-[var(--text-secondary)]">{{ $t('aiProjects.subtitle') }}</p>
      <div class="mt-4 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-4 text-sm text-[var(--text-secondary)]">
        <span class="font-semibold text-[var(--text-primary)]">{{ $t('aiProjects.emphasisTitle') }}</span>
        <span class="ml-2">{{ $t('aiProjects.emphasisText') }}</span>
      </div>
    </section>

    <section class="mt-8 grid gap-4 md:grid-cols-2">
      <article
        v-for="project in aiProjects"
        :key="project.titleKey"
        :id="project.anchorId"
        class="panel p-5"
      >
        <h3 class="text-lg font-semibold text-[var(--text-primary)]">{{ $t(project.titleKey) }}</h3>
        <p class="mt-2 text-sm text-[var(--text-muted)]">{{ $t(project.summaryKey) }}</p>

        <div class="mt-3 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-3">
          <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('aiProjects.labels.capabilities') }}</p>
          <ul class="mt-2 space-y-2 text-sm text-[var(--text-secondary)]">
            <li v-for="item in project.capabilities" :key="item" class="flex gap-2">
              <span class="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
              <span>{{ $t(item) }}</span>
            </li>
          </ul>
        </div>

        <div class="mt-3 rounded-xl border border-[var(--border)] bg-[var(--chip-bg)] p-3">
          <p class="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">{{ $t('aiProjects.labels.stack') }}</p>
          <div class="mt-2 flex flex-wrap gap-2">
            <span
              v-for="stackItem in project.stack"
              :key="`${project.titleKey}-${stackItem}`"
              class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--text-secondary)]"
            >
              {{ stackItem }}
            </span>
          </div>
        </div>

        <div class="mt-3 text-sm text-[var(--accent)]">
          <strong>{{ $t('aiProjects.labels.emphasis') }}:</strong> {{ $t(project.emphasisKey) }}
        </div>

        <div v-if="project.references && project.references.length" class="mt-3 text-sm text-[var(--text-muted)]">
          <span class="font-semibold text-[var(--text-secondary)]">{{ $t('projectsPage.labels.related') }}:</span>
          <div class="mt-2 flex flex-wrap gap-2">
            <router-link
              v-for="ref in project.references"
              :key="ref.labelKey"
              :to="ref.to"
              class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--accent)] hover:underline"
            >
              {{ $t(ref.labelKey) }}
            </router-link>
          </div>
        </div>
      </article>
    </section>

    <section class="mt-8 panel p-6">
      <h2 class="text-xl font-bold text-[var(--text-primary)]">{{ $t('aiPage.references.title') }}</h2>
      <p class="mt-2 text-sm text-[var(--text-muted)]">{{ $t('aiPage.references.subtitle') }}</p>
      <div class="mt-4 flex flex-wrap gap-2">
        <router-link
          v-for="ref in references"
          :key="ref.labelKey"
          :to="ref.to"
          class="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--accent)] hover:underline"
        >
          {{ $t(ref.labelKey) }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AIEngineeringPage',
  data() {
    return {
      aiProjects: [
        {
          anchorId: 'ai-louis',
          titleKey: 'aiProjects.louis.title',
          summaryKey: 'aiProjects.louis.summary',
          capabilities: [
            'aiProjects.louis.capabilities.0',
            'aiProjects.louis.capabilities.1',
            'aiProjects.louis.capabilities.2',
            'aiProjects.louis.capabilities.3',
            'aiProjects.louis.capabilities.4',
            'aiProjects.louis.capabilities.5',
            'aiProjects.louis.capabilities.6',
            'aiProjects.louis.capabilities.7',
            'aiProjects.louis.capabilities.8',
            'aiProjects.louis.capabilities.9',
            'aiProjects.louis.capabilities.10',
          ],
          stack: [
            'Node.js',
            'MongoDB Atlas',
            'ChromaDB',
            'BGE-M3',
            'OpenAI text-embedding-3-small',
            'Docker',
            'Render',
            'SSE streaming',
          ],
          emphasisKey: 'aiProjects.louis.emphasis',
          references: [
            { to: '/projects#project-louis', labelKey: 'aiPage.references.items.louis' },
            { to: '/experience#experience-harbor', labelKey: 'projectsDetailed.references.harborExp' },
          ],
        },
        {
          anchorId: 'ai-cyframe-rag',
          titleKey: 'aiProjects.cyframeRag.title',
          summaryKey: 'aiProjects.cyframeRag.summary',
          capabilities: [
            'aiProjects.cyframeRag.capabilities.0',
            'aiProjects.cyframeRag.capabilities.1',
            'aiProjects.cyframeRag.capabilities.2',
            'aiProjects.cyframeRag.capabilities.3',
            'aiProjects.cyframeRag.capabilities.4',
            'aiProjects.cyframeRag.capabilities.5',
          ],
          stack: [
            'Python',
            'SentenceTransformers',
            'ChromaDB',
            'SVN ingestion',
            'IIS + ASP',
          ],
          emphasisKey: 'aiProjects.cyframeRag.emphasis',
          references: [
            { to: '/projects#project-cyframe-rag', labelKey: 'aiPage.references.items.cyframeRag' },
            { to: '/experience#experience-cyframe', labelKey: 'projectsDetailed.references.cyframeExp' },
          ],
        },
        {
          anchorId: 'ai-cyframe-dev',
          titleKey: 'aiProjects.cyframeDev.title',
          summaryKey: 'aiProjects.cyframeDev.summary',
          capabilities: [
            'aiProjects.cyframeDev.capabilities.0',
            'aiProjects.cyframeDev.capabilities.1',
            'aiProjects.cyframeDev.capabilities.2',
            'aiProjects.cyframeDev.capabilities.3',
          ],
          stack: [
            'Python',
            'Vector DB',
            'Internal docs',
            'Repository connectors',
          ],
          emphasisKey: 'aiProjects.cyframeDev.emphasis',
          references: [
            { to: '/projects#project-cyframe-dev', labelKey: 'aiPage.references.items.cyframeDev' },
            { to: '/experience#experience-cyframe', labelKey: 'projectsDetailed.references.cyframeExp' },
          ],
        },
        {
          anchorId: 'ai-eval-harness',
          titleKey: 'aiProjects.evalHarness.title',
          summaryKey: 'aiProjects.evalHarness.summary',
          capabilities: [
            'aiProjects.evalHarness.capabilities.0',
            'aiProjects.evalHarness.capabilities.1',
            'aiProjects.evalHarness.capabilities.2',
            'aiProjects.evalHarness.capabilities.3',
          ],
          stack: [
            'Python',
            'RAG evaluation',
            'BM25 / Hybrid / Rerank',
          ],
          emphasisKey: 'aiProjects.evalHarness.emphasis',
          references: [
            { to: '/projects#project-eval-harness', labelKey: 'aiPage.references.items.evalHarness' },
            { to: '/experience#experience-cyframe', labelKey: 'projectsDetailed.references.cyframeExp' },
          ],
        },
      ],
      references: [
        { to: '/projects#project-louis', labelKey: 'aiPage.references.items.louis' },
        { to: '/projects#project-cyframe-rag', labelKey: 'aiPage.references.items.cyframeRag' },
        { to: '/projects#project-cyframe-dev', labelKey: 'aiPage.references.items.cyframeDev' },
        { to: '/projects#project-eval-harness', labelKey: 'aiPage.references.items.evalHarness' },
        { to: '/projects#project-harbor-blog', labelKey: 'aiPage.references.items.harborBlog' },
      ],
    };
  },
};
</script>
